import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@vd/ui';
import { AuthLayout } from './AuthLayout';
import { api } from '../../lib/api';
import type { ApiError } from '../../lib/apiClient';

/**
 * Email-verification landing page.
 *
 * The transactional verify-email template (apps/api/src/email/templates.ts)
 * generates links of the shape `/verify#token=<verifyToken>`. Clicking
 * the link lands here. The page auto-POSTs the token to
 * `/auth/verify-email` on mount — no button click — so the email-click
 * itself is the consent gesture.
 *
 * Why the fragment (#) instead of a query (?): browsers never send URL
 * fragments to the server, so the token cannot appear in same-origin
 * access logs or in the Referer header on the subsequent API POST.
 * The token only exists in the user's email client + their local
 * browser memory + (briefly) `location.hash`. We strip even that via
 * `history.replaceState` immediately after reading it.
 *
 * Legacy query-string links (`/verify?token=...`) sent before the
 * fragment migration on 2026-05-20 are still in inboxes for up to the
 * token TTL (24h). The page reads BOTH shapes — fragment first, query
 * as fallback — so legacy links continue to work. Once the youngest
 * legacy email expires, the query-string fallback can be removed.
 *
 * States:
 *   - verifying  → "Verifying your email…"
 *   - success    → confirmation + Sign in CTA
 *   - invalid    → "Link invalid or expired" + sign-up CTA (404 from API)
 *   - retryable  → "Couldn't verify right now" + Try again button (5xx / network)
 *   - no-token   → user hit /verify directly with no query param
 *
 * Strict-mode double-invoke guard: React 18 dev-mode runs effects
 * twice. Without a guard the first call would consume the token and
 * the second would render "invalid" even after success. We track the
 * exact token in `firedTokenRef` (not a boolean) so that a NEW token
 * passed to the same mounted component still triggers verification —
 * matters if React Router preserves the component across search-param
 * changes.
 */
type State =
  | { kind: 'verifying' }
  | { kind: 'success' }
  | { kind: 'invalid' }
  | { kind: 'retryable' }
  | { kind: 'no-token' };

/** Read the verify token from either the URL fragment or query string,
 *  fragment-first. Returns null when neither carries a `token` param.
 *  Pure — no side effects, no DOM mutation. */
function readTokenFromLocation(searchToken: string | null): string | null {
  const hash = typeof window !== 'undefined' ? window.location.hash : '';
  if (hash.startsWith('#')) {
    const fragParams = new URLSearchParams(hash.slice(1));
    const fromHash = fragParams.get('token');
    if (fromHash) return fromHash;
  }
  return searchToken;
}

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = readTokenFromLocation(params.get('token'));
  const [state, setState] = useState<State>(token ? { kind: 'verifying' } : { kind: 'no-token' });
  const firedTokenRef = useRef<string | null>(null);
  const [retryCounter, setRetryCounter] = useState(0);

  useEffect(() => {
    if (!token) return;
    // Don't re-fire for the same token (strict-mode guard). Bumping
    // retryCounter resets the ref so an explicit Try-again replays the
    // same token.
    if (firedTokenRef.current === token && retryCounter === 0) return;
    firedTokenRef.current = token;

    // Strip the token from the visible URL immediately so:
    //   - browser history doesn't keep `?token=...` or `#token=...`
    //   - if the user clicks an in-page link, the Referer header on
    //     that subsequent navigation doesn't leak the token
    //   - back-button doesn't bring them to a URL that would replay
    // Runs once per mount; harmless on the retryable path since the
    // token isn't on the URL anymore.
    if (typeof window !== 'undefined') {
      window.history.replaceState(null, '', '/verify');
    }

    setState({ kind: 'verifying' });
    (async () => {
      try {
        await api.verifyEmail(token);
        setState({ kind: 'success' });
      } catch (err) {
        const status = (err as ApiError)?.status;
        // 404 = token invalid / expired / already consumed (final,
        // recoverable only by signing up again). Anything else (0 for
        // network, 5xx for server errors) is potentially transient —
        // offer a retry instead of telling the user to start over.
        const transient = status !== 404;
        console.error('[verify] verifyEmail failed:', { status, err });
        setState({ kind: transient ? 'retryable' : 'invalid' });
      }
    })();
    // retryCounter intentionally in deps so the Try-again button re-runs the effect.
  }, [token, retryCounter]);

  if (state.kind === 'no-token') {
    return (
      <AuthLayout title="Link invalid" footer={<Link to="/signin">Back to sign in</Link>}>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          This verification link is missing a token. Check the email and try the link
          again, or sign up to receive a fresh verification email.
        </p>
      </AuthLayout>
    );
  }

  if (state.kind === 'verifying') {
    return (
      <AuthLayout title="Verifying your email…">
        <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
          One moment while we confirm the token from your inbox.
        </p>
      </AuthLayout>
    );
  }

  if (state.kind === 'success') {
    return (
      <AuthLayout
        title="Email verified"
        subtitle="Your account is all set."
        footer={<Link to="/signin">Sign in</Link>}
      >
        <Button
          variant="primary"
          size="lg"
          style={{ width: '100%' }}
          onClick={() => navigate('/signin', { replace: true })}
        >
          Continue to sign in
        </Button>
      </AuthLayout>
    );
  }

  if (state.kind === 'retryable') {
    return (
      <AuthLayout
        title="Couldn't verify right now"
        footer={<Link to="/signin">Back to sign in</Link>}
      >
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, marginBottom: 12 }}>
          A network or server hiccup blocked the verification. Your link is still
          valid; try again in a moment.
        </p>
        <Button
          variant="primary"
          size="lg"
          style={{ width: '100%' }}
          onClick={() => setRetryCounter((n) => n + 1)}
        >
          Try again
        </Button>
      </AuthLayout>
    );
  }

  // state.kind === 'invalid'
  return (
    <AuthLayout
      title="Link invalid or expired"
      footer={<Link to="/signup">Sign up again</Link>}
    >
      <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
        This verification link is either expired or has already been used. Sign up
        again to receive a fresh verification email, or contact support if your
        account already exists.
      </p>
    </AuthLayout>
  );
}
